// API service for hostbento onboarding
// The API endpoints are scoped to hostbento app path.
import type { TaskProposal } from "@/apps/hb/flows/onboarding/types";
import { ApiService } from "./base";

interface IntentStatusResponse {
  processing_message: string;
  progress_percentage: number;
  completed_at: string | null;
  task_proposals: TaskProposal[];
}

interface CreateIntentRequest {
  about_website: string;
  project_name?: string;
  website_url?: string;
  generate_proposals?: boolean;
}

interface CreateIntentResponse {
  intent_id: string;
  proposals: TaskProposal[];
}

interface StartFromIntentResponse {
  site_id: string;
  project_name: string;
  site_url: string;
  project_id: string;
  mcp_server_url: string;
  proposals: TaskProposal[];
  template_name: string | null;
}

interface VerifyConnectionRequest {
  project_id: string;
}

interface VerifyConnectionResponse {
  connected: boolean;
  message: string;
}

export class HostbentoOnboardingApi extends ApiService {
  private readonly BASE_URL = "/api/v1/onboard";

  async getIntentStatus(intentId: string) {
    return this.request<IntentStatusResponse>(
      `${this.BASE_URL}/intent-status?intent_id=${intentId}`,
    );
  }

  async createIntent(data: CreateIntentRequest) {
    return this.request<CreateIntentResponse>(
      `${this.BASE_URL}/create-intent`,
      { method: "POST", body: data },
    );
  }

  async startFromIntent(intentId: string, templateUrlSlug?: string) {
    return this.request<StartFromIntentResponse>(
      `${this.BASE_URL}/start-from-intent`,
      {
        method: "POST",
        body: {
          intent_id: intentId,
          ...(templateUrlSlug ? { template_url_slug: templateUrlSlug } : {}),
        },
      },
    );
  }

  async verifyConnection(data: VerifyConnectionRequest) {
    return this.request<VerifyConnectionResponse>(
      `${this.BASE_URL}/verify-connection`,
      {
        method: "POST",
        body: data,
      },
    );
  }

  async kickoffProposals(intentId: string) {
    return this.request<{ task_ids: string[] }>(
      `${this.BASE_URL}/kickoff-proposals`,
      {
        method: "POST",
        body: { intent_id: intentId },
      },
    );
  }

  async diveIn(intentId: string) {
    return this.request<{ redirect_url: string }>(`${this.BASE_URL}/dive-in`, {
      method: "POST",
      body: { intent_id: intentId },
    });
  }
}

export const hostbentoOnboardingApi = new HostbentoOnboardingApi();

interface RequestCodeRequest {
  email: string;
  captcha_token: string;
}

interface RequestCodeResponse {
  message: string;
  code?: string;
  expires_in_minutes: number;
}

interface VerifyCodeRequest {
  email: string;
  code: string;
  captcha_token: string;
  next?: string;
  intent?: string;
  intent_id?: string;
}

interface VerifyCodeResponse {
  message: string;
  redirect_url: string;
  new_user: boolean;
}

interface GoogleLoginRequest {
  id_token: string;
  next?: string;
  intent?: string;
  intent_id?: string;
}

interface GoogleLoginResponse {
  message: string;
  redirect_url: string;
  new_user: boolean;
}

export class HostbentoAuthApi extends ApiService {
  private readonly BASE_URL = "/api/v1/auth";

  async requestCode(data: RequestCodeRequest) {
    return this.request<RequestCodeResponse>(`${this.BASE_URL}/request-code`, {
      method: "POST",
      body: data,
    });
  }

  async verifyCode(data: VerifyCodeRequest) {
    return this.request<VerifyCodeResponse>(`${this.BASE_URL}/verify-code`, {
      method: "POST",
      body: data,
    });
  }

  async googleLogin(data: GoogleLoginRequest) {
    return this.request<GoogleLoginResponse>(`${this.BASE_URL}/google-login`, {
      method: "POST",
      body: data,
    });
  }
}

export const hostbentoAuthApi = new HostbentoAuthApi();
